import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { participantColor, PUBLISHER_PARTICIPANT_KEY } from '@srm/screen-sharing/light';
import SupportChatPreview, { type SupportPopupPlacement } from '@molecules/SupportChatPreview';
import SupportShareHint from '@molecules/SupportShareHint';
import { useScreenShareContext } from './ScreenShareContext';
import { SUPPORT_CHAT_TOGGLE_ATTRIBUTE, SUPPORT_MIC_TOGGLE_ATTRIBUTE } from './supportChat';
import { supportOverlayRoot } from './supportPresence';
import { announceSupport } from './supportAnnouncer';
import {
    SUPPORT_CHAT_PREVIEW_TIMEOUT,
    SUPPORT_MIC_TOAST_TEXT,
    SUPPORT_MIC_TOAST_TIMEOUT,
    SUPPORT_SHARE_HINT_KEY,
    supportMicAnnouncement,
} from './supportPopups';

const POPUP_GAP = 10;
/** Над кнопкой — если над ней столько места; полоса показа обычно у верха окна, тогда под ней. */
const ABOVE_ROOM = 150;
const EDGE = 12;

type Popup =
    | { kind: 'message'; key: string; author: string; color?: string; text: string }
    | { kind: 'mic'; key: string; author: string; color?: string };

const hintKey = (userId: string | number) => `${SUPPORT_SHARE_HINT_KEY}:${userId}`;

const hintSeen = (userId: string | number) => {
    try {
        return localStorage.getItem(hintKey(userId)) === '1';
    } catch {
        return false;
    }
};

const markHintSeen = (userId: string | number) => {
    try {
        localStorage.setItem(hintKey(userId), '1');
    } catch {
        /* Хранилище закрыто: подсказка покажется ещё раз в следующем показе. */
    }
};

/** После действия во всплывающем фокус — на кнопку полосы, а не в `body`. */
const focusBarButton = (attribute: string) =>
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[${attribute}]`)?.focus());

interface IAnchor {
    placement: SupportPopupPlacement;
    right: number;
    /** От низа окна (над кнопкой) или от верха (под ней) */
    offset: number;
    /** Хвостик превью — к «Чат», подсказки — к середине «Микрофон» + «Чат» */
    arrow: number;
    arrowGroup: number;
}

const sameAnchor = (a: IAnchor, b: IAnchor) =>
    a.placement === b.placement &&
    a.right === b.right &&
    a.offset === b.offset &&
    a.arrow === b.arrow &&
    a.arrowGroup === b.arrowGroup;

const anchorStyle = (anchor: IAnchor) =>
    ({
        position: 'fixed',
        right: anchor.right,
        ...(anchor.placement === 'above' ? { bottom: anchor.offset } : { top: anchor.offset }),
        '--support-popup-arrow': `${anchor.arrow}px`,
        '--support-popup-arrow-group': `${anchor.arrowGroup}px`,
    }) as unknown as CSSProperties;

/**
 * Место у кнопки «Чат» (и «Микрофон» рядом): правый край карточки — по правому краю кнопок, хвостик — к их центру.
 * Пересчёт — по размеру кнопок, полосы и страницы (счётчик, индикатор уровня, смонтированная оболочка), окну и
 * прокрутке.
 */
function useChatAnchor(enabled: boolean): IAnchor | null {
    const [anchor, setAnchor] = useState<IAnchor | null>(null);
    useLayoutEffect(() => {
        if (!enabled) {
            setAnchor(null);
            return undefined;
        }
        let watched: Element[] = [];
        const measure = () => {
            const toggle = document.querySelector<HTMLElement>(`[${SUPPORT_CHAT_TOGGLE_ATTRIBUTE}]`);
            const mic = document.querySelector<HTMLElement>(`[${SUPPORT_MIC_TOGGLE_ATTRIBUTE}]`);
            // Кнопки пересоздаются (плавающая полоса сменилась полосой оболочки) — следить за новыми.
            const nodes = [document.body, toggle, mic, toggle?.closest('.support-bar') ?? null].filter(
                (node): node is HTMLElement => !!node,
            );
            if (nodes.length !== watched.length || nodes.some((node, index) => node !== watched[index])) {
                observer?.disconnect();
                watched = nodes;
                nodes.forEach((node) => observer?.observe(node));
            }
            if (!toggle) {
                setAnchor(null);
                return;
            }
            const rect = toggle.getBoundingClientRect();
            const left = Math.min(rect.left, mic?.getBoundingClientRect().left ?? rect.left);
            const right = Math.max(EDGE, window.innerWidth - rect.right);
            const edge = window.innerWidth - right;
            const placement: SupportPopupPlacement = rect.top > ABOVE_ROOM ? 'above' : 'below';
            const next: IAnchor = {
                placement,
                right,
                offset: placement === 'above' ? window.innerHeight - rect.top + POPUP_GAP : rect.bottom + POPUP_GAP,
                arrow: Math.max(16, Math.round(edge - (rect.left + rect.right) / 2)),
                arrowGroup: Math.max(16, Math.round(edge - (left + rect.right) / 2)),
            };
            setAnchor((previous) => (previous && sameAnchor(previous, next) ? previous : next));
        };
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [enabled]);
    return anchor;
}

/**
 * Всплывающие у кнопки «Чат» полосы показа (IMPLEMENTATION.md §30, §31): превью нового сообщения специалиста, пока
 * чат свёрнут; «специалист включил микрофон» при выключенном своём; подсказка про чат и микрофон в первом показе. Всё —
 * в слое оверлеев поддержки: вне захвата и вне удалённого управления.
 */
export default function SupportBarPopups({ userId }: { userId?: string | number }) {
    const share = useScreenShareContext();
    const { active, suspended, chatOpened, setChatOpened, onChatIncoming, toggleMic } = share;
    const [popup, setPopup] = useState<Popup | null>(null);
    const [hint, setHint] = useState(false);
    const chatOpenedRef = useRef(chatOpened);
    chatOpenedRef.current = chatOpened;
    const participants = useRef(share.participants);
    participants.current = share.participants;

    // Новое сообщение специалиста при свёрнутом чате — превью у кнопки (новое заменяет прежнее) и диктору (раскрытую
    // панель объявляет её лента, `role="log"`).
    useEffect(
        () =>
            onChatIncoming((message) => {
                if (chatOpenedRef.current || message.mine) return;
                announceSupport(`Новое сообщение в чате от ${message.authorName}`);
                const color =
                    message.color ?? participants.current.find((person) => person.name === message.authorName)?.color;
                setPopup({
                    kind: 'message',
                    key: message.key,
                    author: message.authorName || 'Поддержка',
                    color: color === undefined ? undefined : participantColor(color),
                    text: message.text,
                });
            }),
        [onChatIncoming],
    );
    useEffect(() => {
        if (chatOpened) setPopup((current) => (current?.kind === 'message' ? null : current));
    }, [chatOpened]);
    // Плашка просьбы сотрудника (жест, диагностика) — под полосой, ровно там, где всплывают подсказка и превью у «Чата»:
    // пока она открыта, они ждут — иначе закрывают её кнопки.
    const asking = !!share.gesture || !!share.diagnostics;
    const visible = active && !asking;
    // Пока открыта подсказка или плашка просьбы, превью и карточка ждут: их срок начинается, когда их видно.
    useEffect(() => {
        if (!popup || hint || !visible) return undefined;
        const timer = setTimeout(
            () => setPopup(null),
            popup.kind === 'mic' ? SUPPORT_MIC_TOAST_TIMEOUT : SUPPORT_CHAT_PREVIEW_TIMEOUT,
        );
        return () => clearTimeout(timer);
    }, [popup, hint, visible]);

    // Специалист впервые за показ включил микрофон, а свой выключен — предложить включить.
    const micNoticed = useRef(false);
    const speakers = share.voice.active.filter((key) => key !== PUBLISHER_PARTICIPANT_KEY);
    const firstSpeaker = speakers.length
        ? share.participants.find((person) => speakers.includes(person.peerId))
        : undefined;
    const micOff = share.voice.mic === 'off';
    const voiceUsable = share.voice.available;
    useEffect(() => {
        if (!active) {
            micNoticed.current = false;
            return;
        }
        // На паузе (§35) микрофона в полосе нет и соединений тоже: карточка ждёт «Продолжить показ».
        if (suspended || !firstSpeaker || micNoticed.current) return;
        micNoticed.current = true;
        if (!micOff || !voiceUsable) return;
        setPopup({
            kind: 'mic',
            key: `mic-${firstSpeaker.peerId}`,
            author: firstSpeaker.name,
            color: participantColor(firstSpeaker.color),
        });
        announceSupport(supportMicAnnouncement(firstSpeaker.name));
    }, [active, suspended, firstSpeaker, micOff, voiceUsable]);
    useEffect(() => {
        if (suspended) setPopup((current) => (current?.kind === 'mic' ? null : current));
    }, [suspended]);
    useEffect(() => {
        if (!micOff) setPopup((current) => (current?.kind === 'mic' ? null : current));
    }, [micOff]);

    // Подсказка — в первом показе, когда специалист подключился (видны «Чат» и «Микрофон»). Пока профиль не загружен,
    // она ждёт: общий ключ без пользователя спрятал бы её от следующего.
    const watched = active && !suspended && share.participants.length > 0;
    const voiceHinted = share.voice.unavailable !== 'disabled';
    useEffect(() => {
        if (!watched || userId === undefined || hintSeen(userId)) return;
        markHintSeen(userId);
        setHint(true);
        announceSupport(
            voiceHinted
                ? 'Подсказка: напишите специалисту в чат или включите микрофон, чтобы поговорить'
                : 'Подсказка: напишите специалисту в чат',
        );
    }, [watched, userId, voiceHinted]);
    useEffect(() => {
        if (!active) {
            setHint(false);
            setPopup(null);
        }
    }, [active]);

    const anchor = useChatAnchor(visible && (hint || !!popup));
    if (!visible || !anchor) return null;

    const position = anchorStyle(anchor);
    let content = null;
    if (hint)
        content = (
            <SupportShareHint
                voice={voiceHinted}
                placement={anchor.placement}
                style={position}
                onClose={() => setHint(false)}
            />
        );
    else if (popup?.kind === 'message')
        content = (
            <SupportChatPreview
                key={popup.key}
                author={popup.author}
                color={popup.color}
                text={popup.text}
                placement={anchor.placement}
                style={position}
                onOpen={() => {
                    setPopup(null);
                    setChatOpened(true);
                }}
                onClose={() => setPopup(null)}
            />
        );
    else if (popup?.kind === 'mic')
        content = (
            <SupportChatPreview
                key={popup.key}
                kind="mic"
                author={popup.author}
                color={popup.color}
                text={SUPPORT_MIC_TOAST_TEXT}
                actionLabel="Включить микрофон"
                onAction={() => {
                    setPopup(null);
                    void toggleMic();
                    focusBarButton(SUPPORT_MIC_TOGGLE_ATTRIBUTE);
                }}
                placement={anchor.placement}
                style={position}
                onClose={() => setPopup(null)}
            />
        );
    return content && createPortal(content, supportOverlayRoot());
}
